import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AuthUserContext } from '../../../components/Session';
import BlocksPalette from '../components/BlocksPalette';
import VariablesPanel from '../components/VariablesPanel';
import ClauseLibrary from '../components/ClauseLibrary';
import PreviewPane from '../components/PreviewPane';
import GenerateWizardModal from '../components/GenerateWizardModal';
import { templatesGet, templatesUpdate } from '../utils/templatesApi';
import '../templates.css';

const TemplateEditorPage = ({ firebase }) => {
    const { templateId } = useParams();
    const history = useHistory();
    const authUser = useContext(AuthUserContext);

    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saveStatus, setSaveStatus] = useState('saved'); // saved, saving, unsaved
    const [showPreview, setShowPreview] = useState(false);
    const [showGenerate, setShowGenerate] = useState(false);

    const saveTimeoutRef = useRef(null);

    // Load template
    useEffect(() => {
        if (!firebase || !templateId) return;

        const load = async () => {
            setLoading(true);
            try {
                const data = await templatesGet(firebase, templateId);
                setTemplate(data);
            } catch (err) {
                console.error('Failed to load template:', err);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [firebase, templateId]);

    // Auto-save with debounce
    const saveTemplate = useCallback(async (data) => {
        if (!firebase || !templateId) return;

        setSaveStatus('saving');
        try {
            await templatesUpdate(firebase, templateId, data);
            setSaveStatus('saved');
        } catch (err) {
            console.error('Failed to save template:', err);
            setSaveStatus('unsaved');
        }
    }, [firebase, templateId]);

    const scheduleAutoSave = useCallback((newTemplate) => {
        setSaveStatus('unsaved');
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
            saveTemplate(newTemplate);
        }, 800);
    }, [saveTemplate]);

    // Update template field
    const updateTemplate = useCallback((field, value) => {
        setTemplate((prev) => {
            const updated = { ...prev, [field]: value, updatedAt: new Date().toISOString() };
            scheduleAutoSave(updated);
            return updated;
        });
    }, [scheduleAutoSave]);

    // Block operations
    const addBlock = useCallback((type) => {
        const newBlock = {
            id: `b-${Date.now()}`,
            type,
            text: type === 'heading' ? 'New Heading' : type === 'signature' ? 'Signature Block' : '',
            optional: type === 'clause',
            enabled: true,
        };
        setTemplate((prev) => {
            const updated = {
                ...prev,
                blocks: [...(prev.blocks || []), newBlock],
                updatedAt: new Date().toISOString(),
            };
            scheduleAutoSave(updated);
            return updated;
        });
    }, [scheduleAutoSave]);

    const updateBlock = useCallback((blockId, field, value) => {
        setTemplate((prev) => {
            const updated = {
                ...prev,
                blocks: prev.blocks.map((b) =>
                    b.id === blockId ? { ...b, [field]: value } : b
                ),
                updatedAt: new Date().toISOString(),
            };
            scheduleAutoSave(updated);
            return updated;
        });
    }, [scheduleAutoSave]);

    const deleteBlock = useCallback((blockId) => {
        setTemplate((prev) => {
            const updated = {
                ...prev,
                blocks: prev.blocks.filter((b) => b.id !== blockId),
                updatedAt: new Date().toISOString(),
            };
            scheduleAutoSave(updated);
            return updated;
        });
    }, [scheduleAutoSave]);

    const moveBlock = useCallback((blockId, direction) => {
        setTemplate((prev) => {
            const blocks = [...prev.blocks];
            const index = blocks.findIndex((b) => b.id === blockId);
            if (index === -1) return prev;

            const newIndex = direction === 'up' ? index - 1 : index + 1;
            if (newIndex < 0 || newIndex >= blocks.length) return prev;

            [blocks[index], blocks[newIndex]] = [blocks[newIndex], blocks[index]];

            const updated = { ...prev, blocks, updatedAt: new Date().toISOString() };
            scheduleAutoSave(updated);
            return updated;
        });
    }, [scheduleAutoSave]);

    // Variable operations
    const addVariable = useCallback((variable) => {
        setTemplate((prev) => {
            const updated = {
                ...prev,
                variables: [...(prev.variables || []), variable],
                updatedAt: new Date().toISOString(),
            };
            scheduleAutoSave(updated);
            return updated;
        });
    }, [scheduleAutoSave]);

    const updateVariable = useCallback((key, updates) => {
        setTemplate((prev) => {
            const updated = {
                ...prev,
                variables: prev.variables.map((v) =>
                    v.key === key ? { ...v, ...updates } : v
                ),
                updatedAt: new Date().toISOString(),
            };
            scheduleAutoSave(updated);
            return updated;
        });
    }, [scheduleAutoSave]);

    const deleteVariable = useCallback((key) => {
        setTemplate((prev) => {
            const updated = {
                ...prev,
                variables: prev.variables.filter((v) => v.key !== key),
                updatedAt: new Date().toISOString(),
            };
            scheduleAutoSave(updated);
            return updated;
        });
    }, [scheduleAutoSave]);

    // Add clause from library
    const addClauseFromLibrary = useCallback((clause) => {
        const newBlock = {
            id: `b-${Date.now()}`,
            type: 'clause',
            text: clause.text,
            optional: true,
            enabled: true,
        };
        setTemplate((prev) => {
            const updated = {
                ...prev,
                blocks: [...(prev.blocks || []), newBlock],
                updatedAt: new Date().toISOString(),
            };
            scheduleAutoSave(updated);
            return updated;
        });
    }, [scheduleAutoSave]);

    // Publish template
    const handlePublish = useCallback(async () => {
        if (!firebase || !templateId || !template) return;

        try {
            await templatesUpdate(firebase, templateId, {
                ...template,
                status: 'published',
                publishedVersion: (template.version || 1),
            });
            setTemplate((prev) => ({ ...prev, status: 'published' }));
            setSaveStatus('saved');
        } catch (err) {
            console.error('Failed to publish:', err);
        }
    }, [firebase, templateId, template]);

    if (loading) {
        return (
            <div className="template-editor-page">
                <div style={{ padding: '60px', textAlign: 'center', color: 'rgba(248,248,248,0.5)' }}>
                    Loading template...
                </div>
            </div>
        );
    }

    if (!template) {
        return (
            <div className="template-editor-page">
                <div style={{ padding: '60px', textAlign: 'center', color: 'rgba(248,248,248,0.5)' }}>
                    Template not found.
                </div>
            </div>
        );
    }

    return (
        <div className="template-editor-page">
            {/* Top Bar */}
            <div className="template-editor-topbar">
                <button
                    className="editor-back-btn"
                    onClick={() => history.push('/franchise-dashboard/templates')}
                >
                    ← Back
                </button>

                <input
                    type="text"
                    className="editor-title-input"
                    value={template.title || ''}
                    onChange={(e) => updateTemplate('title', e.target.value)}
                    placeholder="Template name..."
                />

                <span className={`editor-status-badge ${saveStatus}`}>
                    {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Unsaved'}
                </span>

                <div className="editor-actions">
                    <button className="editor-btn secondary" onClick={() => setShowPreview(true)}>
                        Preview
                    </button>
                    <button className="editor-btn secondary" onClick={handlePublish}>
                        Publish
                    </button>
                    <button className="editor-btn primary" onClick={() => setShowGenerate(true)}>
                        Generate
                    </button>
                </div>
            </div>

            {/* Editor Layout */}
            <div className="template-editor-layout">
                {/* Left Sidebar - Blocks Palette */}
                <div className="editor-panel editor-sidebar-left">
                    <BlocksPalette onAddBlock={addBlock} />
                </div>

                {/* Main Editor */}
                <div className="editor-main">
                    {template.blocks?.length === 0 ? (
                        <div className="templates-empty">
                            <p>No blocks yet. Add blocks from the left panel.</p>
                        </div>
                    ) : (
                        template.blocks?.map((block, index) => (
                            <div key={block.id} className="block-editor-item">
                                <div className="block-editor-header">
                                    <span className="block-type-label">{block.type}</span>
                                    {block.type === 'clause' && (
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'rgba(248,248,248,0.6)' }}>
                                            <input
                                                type="checkbox"
                                                checked={block.optional || false}
                                                onChange={(e) => updateBlock(block.id, 'optional', e.target.checked)}
                                            />
                                            Optional
                                        </label>
                                    )}
                                    <div className="block-actions">
                                        <button
                                            className="block-action-btn"
                                            onClick={() => moveBlock(block.id, 'up')}
                                            disabled={index === 0}
                                        >
                                            ↑
                                        </button>
                                        <button
                                            className="block-action-btn"
                                            onClick={() => moveBlock(block.id, 'down')}
                                            disabled={index === template.blocks.length - 1}
                                        >
                                            ↓
                                        </button>
                                        <button
                                            className="block-action-btn delete"
                                            onClick={() => deleteBlock(block.id)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                                <div className="block-editor-content">
                                    <textarea
                                        value={block.text || ''}
                                        onChange={(e) => updateBlock(block.id, 'text', e.target.value)}
                                        placeholder={`Enter ${block.type} content... Use {{variable_name}} for placeholders`}
                                    />
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Right Sidebar - Variables & Clauses */}
                <div className="editor-panel editor-sidebar-right">
                    <VariablesPanel
                        variables={template.variables || []}
                        onAdd={addVariable}
                        onUpdate={updateVariable}
                        onDelete={deleteVariable}
                    />
                    <div style={{ marginTop: '24px' }}>
                        <ClauseLibrary onAddClause={addClauseFromLibrary} />
                    </div>
                </div>
            </div>

            {/* Preview Modal */}
            {showPreview && (
                <div className="modal-overlay" onClick={() => setShowPreview(false)}>
                    <div
                        className="modal-container"
                        style={{ maxWidth: '900px' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-header">
                            <h2>Preview</h2>
                            <button className="modal-close" onClick={() => setShowPreview(false)}>
                                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <PreviewPane template={template} values={{}} />
                        </div>
                    </div>
                </div>
            )}

            {/* Generate Wizard */}
            {showGenerate && (
                <GenerateWizardModal
                    firebase={firebase}
                    template={template}
                    onClose={() => setShowGenerate(false)}
                />
            )}
        </div>
    );
};

export default TemplateEditorPage;
